import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
} from 'react-hook-form-mui';
import { Card, CardContent, CardHeader, Stack } from '@mui/material';

import {
    BusinessInput,
    EditButtons,
    ProductInput,
    ProductSubtypeInput,
    ProductTypeInput,
} from '@barboxx/admin-components';
import { BUSINESS_DEFAULT_PRODUCT_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { BusinessDefaultProduct } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';

interface Props {
    businessDefaultProduct: BusinessDefaultProduct;
}

export const BusinessDefaultProductDetailsCard = ({
    businessDefaultProduct,
}: Props) => {
    const [editable, editableActions] = useBool(false);
    const notifications = useNotifications();

    const mutateBusinessDefaultProduct = useMutation({
        mutationKey: ['businessDefaultProduct', businessDefaultProduct.id],
        mutationFn: (data: BusinessDefaultProduct.UpdateRequest) =>
            Api.BusinessSpecific.DefaultProduct.putDefaultProduct(
                businessDefaultProduct.id,
                data
            ),
    });

    const formContext = useForm<BusinessDefaultProduct.UpdateRequest>({
        resolver: yupResolver(BUSINESS_DEFAULT_PRODUCT_SCHEMA),
        defaultValues: businessDefaultProduct,
    });

    const handleCancel = () => {
        editableActions.off();
        formContext.reset(undefined);
    };

    const onSubmit = async (values: BusinessDefaultProduct.UpdateRequest) => {
        mutateBusinessDefaultProduct
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show('Updated default product!', {
                    severity: 'success',
                });

                editableActions.off();
                formContext.reset(data);
            })
            .catch(e => {
                notifications.show('Error Updating default product!', {
                    severity: 'error',
                });
            });
    };

    return (
        <Card>
            <CardHeader title="BusinessDefaultProduct Details" />
            <CardContent>
                <FormContainer onSuccess={onSubmit} formContext={formContext}>
                    <Stack gap={2}>
                        <BusinessInput disabled={!editable} />
                        <ProductInput disabled={!editable} />
                        <ProductTypeInput disabled={!editable} />
                        <ProductSubtypeInput disabled={!editable} />
                        <TextFieldElement
                            label="Notes"
                            name="notes"
                            fullWidth
                            disabled={!editable}
                        />
                        <CheckboxElement
                            label="Active"
                            name="isActive"
                            disabled={!editable}
                        />
                        <Can I="update" a="BusinessDefaultProduct">
                            <EditButtons
                                formContext={formContext}
                                handleCancel={handleCancel}
                                editable={editable}
                                editableActions={editableActions}
                            />
                        </Can>
                    </Stack>
                </FormContainer>
            </CardContent>
        </Card>
    );
};
